<template>
  <Dialog
    :visible="visible"
    modal
    :header="
      props.skipAttendee
        ? defaultAttendeeName + ' Signature'
        : selectedUser.length > 0
          ? selectedUser[0].value
          : 'Attendee Signature'
    "
    :style="{ width: '50rem' }"
    :closable="false"
    @show="onShow"
  >
    <template #header>
      <div class="flex justify-between items-center w-full">
        <span>
          {{
            props.skipAttendee
              ? defaultAttendeeName + " Signature"
              : selectedUser.length > 0
                ? selectedUser[0].value
                : "Attendee Signature"
          }}
        </span>
        <Button
          icon="pi pi-times"
          severity="secondary"
          @click="onCancel"
          text
          rounded
        />
      </div>
    </template>
    <div class="flex flex-col w-full h-full">
      <Stepper v-model:value="activeStep">
        <StepList v-if="!props.skipAttendee">
          <Step :value="1">Attendee</Step>
          <Step
            :value="2"
            @click="validateAttendees"
          >
            Signature
          </Step>
        </StepList>
        <StepPanels>
          <StepPanel
            v-if="!props.skipAttendee"
            :value="1"
          >
            <div class="flex flex-col h-48">
              <div
                class="p-4 border-surface-200 rounded bg-surface-50 flex-auto flex justify-center items-center font-medium"
              >
                <InputGroup>
                  <AutoComplete
                    ref="autoCompleteRef"
                    v-model="selectedUser"
                    :suggestions="employeeList"
                    optionLabel="label"
                    :placeholder="placeholderText"
                    @complete="search"
                    :forceSelection="false"
                    multiple
                    size="large"
                    @item-select="onSelectUser"
                    @keydown.enter.prevent="onEnter"
                    @keydown="preventTyping"
                    @blur="onEnter"
                    :loading="false"
                    fluid
                    :optionDisabled="(option) => selectedUser.includes(option)"
                    @click="openDropdown"
                    :class="[
                      'disable-input',
                      { 'no-cursor': selectedUser.length > 0 }
                    ]"
                  >
                    <template #empty>
                      <div>Press Enter to add name</div>
                    </template>
                    <template #chip="{ value }">
                      <Chip
                        class="py-0 pl-0 pr-4"
                        removable
                        @remove="removeChip(value)"
                      >
                        <span
                          class="bg-primary text-primary-contrast rounded-full w-8 h-8 flex items-center justify-center"
                        >
                          {{ value.label[0].toUpperCase() }}
                        </span>
                        <span class="ml-2 font-medium">{{ value.label }}</span>
                      </Chip>
                    </template>
                  </AutoComplete>
                  <InputGroupAddon>
                    <Select
                      v-if="
                        selectedUser.length > 0 &&
                        selectedUser[0]?.id === undefined
                      "
                      v-model="selUserType"
                      :options="userType"
                      optionLabel="name"
                      optionValue="value"
                      placeholder="Select a Type"
                      class="w-full md:w-56"
                    />

                    <i
                      v-else
                      class="pi pi-id-card"
                    />
                  </InputGroupAddon>
                </InputGroup>
              </div>
            </div>
            <div class="flex pt-6 justify-end">
              <Button
                label="Next"
                icon="pi pi-arrow-right"
                @click="validateAttendees"
              />
            </div>
          </StepPanel>
          <StepPanel
            :value="2"
            v-if="activeStep === 2"
          >
            <div class="flex flex-col">
              <div
                class="border-2 border-dashed border-surface-200 rounded bg-surface-50 flex-auto flex justify-center items-center font-medium"
              >
                <div class="flex h-full w-full justify-center">
                  <VueSignaturePad
                    ref="signaturePad"
                    height="20vh"
                    width="100%"
                    :options="{
                      penColor: state.option.penColor,
                      backgroundColor: state.option.backgroundColor
                    }"
                  />
                </div>
              </div>
            </div>
            <div class="flex pt-6 justify-between">
              <Button
                v-if="!props.skipAttendee"
                label="Back"
                severity="secondary"
                icon="pi pi-arrow-left"
                @click="activeStep = 1"
              />

              <Button
                label="Clear"
                class="p-button-secondary"
                icon="pi pi-times"
                @click="onClear"
              />
              <Button
                label="Finish"
                icon="pi pi-save"
                iconPos="right"
                @click="onConfirm"
              />
            </div>
          </StepPanel>
        </StepPanels>
      </Stepper>
    </div>
  </Dialog>
</template>

<script setup>
  const props = defineProps({
    visible: {
      type: Boolean,
      required: true
    },
    skipAttendee: {
      type: Boolean,
      default: false
    },
    attendeeName: {
      type: String,
      default: ""
    }
  });
  import { ref, computed, reactive, nextTick } from "vue";
  import { VueSignaturePad } from "@selemondev/vue3-signature-pad";
  import Helper from "@/utils/helper";
  import { useEmployeeStore } from "@/stores/employeeStore";
  import { useRoute } from "vue-router";
  const route = useRoute();

  const signaturePad = ref();
  const isLoading = ref(false);
  const activeStep = ref(1);
  const employeeList = ref([]);
  const selectedUser = ref([]);
  const selUserType = ref("customer");
  const emit = defineEmits(["update:visible", "onConfirm"]);
  const autoCompleteRef = ref(null);

  // Computed property to get the default attendee name
  const defaultAttendeeName = computed(() => {
    if (props.attendeeName) {
      return props.attendeeName;
    }
    const queryPairs = Helper.getQueryPairs(route);
    // if operator found then override the reportedBy with the operator
    if (queryPairs.operator) {
      return queryPairs.operator;
    }
    return Helper.getUserName() || "Current User";
  });

  // Open Dropdown for Attendees list
  const openDropdown = async () => {
    await search({ query: "" });
    autoCompleteRef.value?.show();
  };

  // Save signature and close dialog
  const onConfirm = () => {
    if (signaturePad.value.isCanvasEmpty()) {
      Helper.showToast("Error", "Please sign before proceeding", "error");
      return;
    }
    const result = signaturePad.value?.saveSignature("image/svg+xml");
    const pkgData = {
      id: Helper.getRandomUuid(),
      signatureData: result,
      attendeeName: props.skipAttendee
        ? defaultAttendeeName.value
        : selectedUser.value[0].value.trim(),
      attendeeType: selectedUser.value[0].id ? "employee" : selUserType.value,
      signatureDate: new Date().toISOString()
    };
    emit("onConfirm", pkgData);
    emit("update:visible", false);
  };

  // Signature pad options
  const state = reactive({
    option: {
      penColor: "rgb(0, 0, 0)",
      backgroundColor: "rgba(0, 0, 0, 0)"
    }
  });

  // Clear signature pad
  const onClear = () => {
    signaturePad.value?.clearCanvas();
  };

  // Go to attendees
  const validateAttendees = () => {
    if (selectedUser.value.length === 0) {
      Helper.showToast("Error", "Please enter an attendee", "error");
      activeStep.value = 1;
      return;
    }
    signaturePad.value?.clearCanvas();
    activeStep.value = 2;
  };

  // User type
  const userType = ref([
    { name: "Contractor", value: "contractor" },
    { name: "Customer", value: "customer" }
  ]);

  // Search employee by name or manually enter name
  const search = async (event) => {
    isLoading.value = true;
    if (selectedUser.value.length > 0) {
      isLoading.value = false;
      return;
    }
    // Search employee by name
    const employeeStore = useEmployeeStore();
    const employees = await employeeStore.getEmployeeList({
      fetchAll: true,
      searchTxt: event.query,
      sortField: "displayName"
    });

    employeeList.value = employees.map((item) => {
      return {
        label: item.displayName,
        value: item.displayName,
        id: item.id
      };
    });
    isLoading.value = false;
  };
  // Placeholder text
  const placeholderText = computed(() => {
    return selectedUser.value.length > 0 ? "" : "Search Name or Enter Name";
  });
  // On Show dialog
  const onShow = async () => {
    if (props.skipAttendee) {
      selectedUser.value = [
        {
          label: Helper.getUserName() || "Current User",
          value: Helper.getUserName() || "Current User",
          id: Helper.getCurrentUserId()
        }
      ];
      await nextTick();
      activeStep.value = 2;
    } else {
      activeStep.value = 1;
      selUserType.value = "customer";
      selectedUser.value = [];
    }
  };

  // Add chip on Enter key
  const onEnter = async (event) => {
    if (selectedUser.value.length > 0) {
      return;
    }
    const typedValue = event.target.value.trim();
    if (typedValue.length === 0) return;
    // Find a matching employee in the employeeList(ignoring case sensitive)
    const matchedEmployee = employeeList.value.find(
      (emp) => emp.label.toLowerCase() === typedValue.toLowerCase()
    );

    // If a matching employee is found, select them
    if (matchedEmployee) {
      selectedUser.value = [matchedEmployee];
    } else if (employeeList.value.length === 1) {
      selectedUser.value = employeeList.value;
    } else {
      // If no match found, add the user as a new entry
      selectedUser.value = [{ label: typedValue, value: typedValue }];
    }
    event.target.value = "";
    event.target.blur();
  };

  // Select only selected user
  const onSelectUser = (event) => {
    selectedUser.value = [event.value];
  };

  // Remove chip
  const removeChip = (value) => {
    selectedUser.value = [];
  };
  // Cancel
  const onCancel = () => {
    emit("update:visible", false);
  };

  // Prevent typing when chip exists
  const preventTyping = (event) => {
    if (selectedUser.value.length > 0 && event.key !== "Backspace") {
      event.preventDefault();
    }
  };
</script>
<style scoped>
  .no-cursor :deep(input) {
    caret-color: transparent;
  }
</style>
